import { CloseOutlined, InfoCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { Flex, Modal, Tabs, Tag } from 'antd';
import type React from 'react';
import { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { LoadingIndicator } from '../../components/LoadingIndicator';
import { OrderType } from '../../graphql/generated/graphql.ts';
import { useCurrentContextStore } from '../../hooks/store/useCurrentContextStore.ts';
import { useDoctorAisExportTypes } from '../../hooks/useDoctorAisExportTypes.ts';
import { patientFullName } from '../../utils/patient.ts';
import { hasFrontDeskRole, hasSomeRole, Role } from '../../utils/user.ts';
import { FilesTab } from './details/FilesTab.tsx';
import { FormsTab } from './details/FormsTab.tsx';
import { LabelsTab } from './details/LabelsTab.tsx';
import { OrderTab } from './details/OrderTab.tsx';
import { ParametersTab } from './details/ParametersTab.tsx';
import { PatientTab } from './details/PatientTab.tsx';
import { RequestTab } from './details/RequestTab.tsx';
import { useRequestDetailsQuery } from './details/useRequestDetailsQuery.tsx';
import { RequestDetailsFooter } from './RequestDetailsFooter.tsx';

export const RequestDetailsModal: React.FC<{
  requestId: string | null;
  onClose: () => void;
  footerExtra?: React.ReactNode;
}> = ({ requestId, onClose, footerExtra }) => {
  const { currentDoctorId, primaryDoctorId } = useCurrentContextStore();
  const auth = useAuth();
  const isFrontDesk = hasFrontDeskRole(auth.user);
  const doctorId = isFrontDesk ? primaryDoctorId : currentDoctorId;

  const [activeKey1, setActiveKey1] = useState('auftrag1');
  const [activeKey2, setActiveKey2] = useState('parameter');

  const { data: currentData, previousData, refetch } = useRequestDetailsQuery(requestId, doctorId);
  const data = currentData ?? previousData;

  // do not refetch (ignores the skip flag) when the requestId is not present anymore (= the dialog isn't open)
  const safeRefetch = () => {
    if (requestId) {
      refetch({ id: requestId });
    }
  };

  const tabs =
    [...(data?.request?.orders ?? [])].sort((a, b) => a.createdAt.localeCompare(b.createdAt)).map((_, index) => `auftrag${index + 1}`) ??
    [];
  // fix out-of-bounds tab key
  if (activeKey1 !== 'auftrag1' && activeKey1.startsWith('auftrag') && !tabs.includes(activeKey1)) {
    setActiveKey1('auftrag1');
  }

  const showPrices = data?.doctor?.showPrices ?? true;
  const flipParamNames = data?.doctor?.flipParamNames ?? false;
  const hasDoctorLabel = (data?.doctor?.zplLabelTemplate ?? '').length > 0;
  const aisExportEnabled =
    useDoctorAisExportTypes(
      hasSomeRole([Role.ROLE_LR_LAB_STAFF, Role.ROLE_LR_FRONT_DESK, Role.ROLE_LR_LAB_ADMIN], auth.user) ? primaryDoctorId : currentDoctorId,
      !requestId
    ).length > 0;

  return (
    <Modal
      title={
        <Flex wrap gap="small" align="center">
          <InfoCircleOutlined />
          <div>Anforderung {data?.request && patientFullName(data.request.patientData)}</div>
          {data?.request?.importType ? <Tag color="warning">Importiert</Tag> : null}
        </Flex>
      }
      okText="Schließen"
      okButtonProps={{ icon: <CloseOutlined /> }}
      open={!!requestId}
      onOk={onClose}
      onCancel={onClose}
      cancelButtonProps={{ hidden: true }}
      footer={(_, { OkBtn }) => <RequestDetailsFooter extra={footerExtra} okButton={<OkBtn />} />}
      width="80%"
      style={{ minWidth: '768px', maxWidth: '1200px' }}
    >
      {!data ? (
        <LoadingIndicator height="200px" />
      ) : (
        <Tabs
          // the modal keeps its content mounted between openings, so remount the tabs per request
          // to reset their local state (comment draft, file preview) instead of carrying it over
          key={data.request?.id}
          activeKey={activeKey1}
          size="small"
          onChange={setActiveKey1}
          items={[
            ...[
              {
                key: 'anforderung',
                label: 'Anforderung',
                children: data?.request ? <RequestTab request={data.request} onRefetchRequested={safeRefetch} /> : null,
              },
              {
                key: 'patient',
                label: 'Patient',
                children: data?.request ? <PatientTab request={data.request} /> : null,
              },
            ],
            ...[...(data?.request?.orders ?? [])]
              .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
              .map((order, index) => {
                const tabs = [
                  {
                    key: 'auftrag',
                    label: 'Auftrag',
                    children: (
                      <OrderTab
                        request={data!.request!}
                        order={order}
                        showPrices={showPrices}
                        importType={data?.request?.importType}
                        hasDoctorLabel={hasDoctorLabel}
                        doctorId={doctorId}
                        aisExportEnabled={aisExportEnabled}
                        onRefetchRequested={safeRefetch}
                      />
                    ),
                  },
                  {
                    key: 'parameter',
                    label: 'Parameter',
                    children: <ParametersTab order={order} showPrices={showPrices} flipParamNames={flipParamNames} />,
                  },
                  {
                    key: 'etiketten',
                    label: 'Etiketten',
                    children: <LabelsTab order={order} requestId={data!.request!.id} onPrinted={safeRefetch} />,
                  },
                  {
                    key: 'files',
                    label: 'Dokumente',
                    children: <FilesTab order={order} requestId={data!.request!.id} onRefetchRequested={safeRefetch} />,
                  },
                  {
                    key: 'forms',
                    label: 'Formulare',
                    children: <FormsTab order={order} />,
                  },
                ];

                return {
                  key: `auftrag${index + 1}`,
                  label:
                    order.type === OrderType.INITIAL ? (
                      <>Auftrag {order.lab.name}</>
                    ) : (
                      <>
                        <PlusCircleOutlined style={{ marginRight: '2px' }} /> Nachf. {order.lab.name}
                      </>
                    ),
                  children: <Tabs type="card" activeKey={activeKey2} onChange={setActiveKey2} items={tabs} />,
                };
              }),
          ]}
        />
      )}
    </Modal>
  );
};
